import { EVENT_SITES } from '@crossbeam/itly';
import { GetApiPayloadByUrl } from '@crossbeam/openapi';

import { sortBy, xor } from 'lodash';
import { storeToRefs } from 'pinia';
import { Ref, computed, onMounted, ref } from 'vue';

import { crossbeamApi } from '@/api';
import useIteratively from '@/composables/useIteratively';
import {
  useDataSharePresetsStore,
  useFlashesStore,
  useSourcesStore,
} from '@/stores';
import { DataSharePreset } from '@/types/data_share_presets';
import { Source, SourceMdm } from '@/types/sources';
import { sortObjectsByMdmType } from '@/utils';

export function useFieldPresets({
  feedId,
  sourceId,
  presetId,
  isForPartner = false,
  onClose,
  onSuccess,
  eventSite,
}: {
  feedId: number;
  sourceId?: number;
  presetId?: number;
  isForPartner?: boolean;
  onClose?: () => void;
  onSuccess?: (presetId: number, isNewPreset: boolean) => void;
  /** Event site for userDefinedSharingPreset (e.g. DATA_PRESETS_MODAL, DATA_PRESETS_TAB). */
  eventSite?: string;
}) {
  const { getSourcesByFeedId } = useSourcesStore();
  const { addSuccessFlash, addErrorFlash } = useFlashesStore();
  const dataSharePresetsStore = useDataSharePresetsStore();
  const { iteratively } = useIteratively();

  const { ready } = storeToRefs(dataSharePresetsStore);

  /**
   * FIELDS LOGIC
   */

  const presetName = ref<string>('');
  const defaultPresetId = ref<number>();
  const allSourceFieldIds = ref<number[]>([]);
  const isToggledNewPreset = ref<boolean>(false);

  const isDefaultPreset = computed(
    () => !!defaultPresetId.value && defaultPresetId.value === presetId,
  );
  const sortedPresets = computed(() => sortNamedPresets(feedId, sourceId));
  const currentSourceFieldIds = computed(() =>
    Object.values(sourceFieldIdsByMdm.value).flatMap((item) => item.ids.value),
  );

  const defaultPresetSourceFieldIds = computed(() =>
    dataSharePresetsStore.getSourceFieldIdsByPresetId(
      feedId,
      sourceId,
      defaultPresetId.value,
    ),
  );
  const hasDefaultPresets = computed(() => {
    const differentIdList = xor(
      defaultPresetSourceFieldIds.value,
      currentSourceFieldIds.value,
    );
    return differentIdList.length === 0;
  });

  const sourceFieldIdsByMdm = computed(() => {
    const syncedSources = getSourcesForPreset(feedId, sourceId).filter(
      (source) => source.sync_enabled,
    );

    const groupedWithTableAndType: Record<
      string,
      { mdm_type: SourceMdm; ids: Ref<number[]> }
    > = {};

    const sortedSources = sortObjectsByMdmType(syncedSources);

    sortedSources.forEach((source) => {
      const sourceFieldIds = source.fields.map(
        (field: { id: number }) => field.id,
      );
      const sourcePresetFieldIds = allSourceFieldIds.value.filter((id) =>
        sourceFieldIds.includes(id),
      );
      groupedWithTableAndType[source.table] = {
        mdm_type: source.mdm_type as SourceMdm,
        ids: ref(sourcePresetFieldIds),
      };
    });
    return groupedWithTableAndType;
  });

  function resetToDefault() {
    allSourceFieldIds.value = [...defaultPresetSourceFieldIds.value];
  }

  function getFieldOptions(mdmType: SourceMdm) {
    const sourcesByFeedId = getSourcesForPreset(feedId, sourceId);
    const source = sourcesByFeedId?.find(
      (source) => source.mdm_type === mdmType,
    );
    const fields = source?.fields.filter((f) => f.is_filterable);

    const fieldOptions = fields?.map((field) => ({
      label: field.nickname,
      value: field.id,
    }));
    return fieldOptions;
  }

  onMounted(async () => {
    if (!onClose) return; // Not running it for FieldPresetTab

    // Running for the FieldPresetsModal
    await dataSharePresetsStore.readySync;
    defaultPresetId.value = dataSharePresetsStore.getDefaultPresetId(
      feedId,
      sourceId,
    );
    allSourceFieldIds.value = dataSharePresetsStore.getSourceFieldIdsByPresetId(
      feedId,
      sourceId,
      presetId || defaultPresetId.value,
    );
    presetName.value = dataSharePresetsStore.getPresetName(
      feedId,
      sourceId,
      presetId || defaultPresetId.value,
      !presetId,
    );
  });

  /**
   * API METHODS
   */

  const isLoading = ref(false);

  // CREATE PRESET
  async function createPreset(name: string, fieldIds: number[]) {
    isLoading.value = true;
    const isSingleShareWithoutName = isForPartner && !isToggledNewPreset.value;
    const body: GetApiPayloadByUrl<'/v0.1/data-share-presets', 'post'> = {
      name: isSingleShareWithoutName ? null : name,
      preset_type: isSingleShareWithoutName
        ? 'single_share_preset'
        : 'named_preset',
      feed_id: feedId,
      base_source_id: sourceId || null, // has only a number if csv or gsheet
      source_field_ids: fieldIds,
    };

    const { data, error } = await crossbeamApi.POST(
      '/v0.1/data-share-presets',
      { body },
    );

    handleErrorAndSuccess(
      `An error occurred while trying to create ${name} preset`,
      error,
      () =>
        redirectAndSuccess(isSingleShareWithoutName ? null : name, data, true),
    );
  }

  // PATCH PRESET
  async function editPreset(
    id: number,
    name: string | null,
    fieldIds: number[],
  ) {
    isLoading.value = true;

    const body: GetApiPayloadByUrl<'/v0.1/data-share-presets/{id}', 'patch'> = {
      name,
      source_field_ids: fieldIds,
    };

    const { data, error } = await crossbeamApi.PATCH(
      '/v0.1/data-share-presets/{id}',
      { body, params: { path: { id } } },
    );

    handleErrorAndSuccess(
      `An error occurred while trying to edit ${name} preset`,
      error,
      () => redirectAndSuccess(name, data),
    );
  }

  // DELETE PRESET
  async function deletePreset(id: number, name: string) {
    isLoading.value = true;

    const { data, error } = await crossbeamApi.DELETE(
      '/v0.1/data-share-presets/{id}',
      { params: { path: { id } } },
    );

    handleErrorAndSuccess(
      `An error occurred while trying to delete ${name} preset`,
      error,
      () => handleSuccess(data, `${name} preset has been successfully deleted`),
    );
  }

  /**
   * HELPERS
   */

  function getSourcesForPreset(feedId: number, sourceId?: number): Source[] {
    let sources = getSourcesByFeedId(feedId);
    if (sourceId) {
      const baseSource = sources.find((source) => source.id === sourceId);
      sources = sources.filter(
        (source) => source.dataset_name === baseSource?.dataset_name,
      );
    }
    return sources;
  }

  function sortNamedPresets(feedId: number, sourceId?: number) {
    const filteredPresets = dataSharePresetsStore
      .getFilteredPresetsByFeeId(feedId, sourceId)
      .map((preset) => ({
        ...preset,
        name: dataSharePresetsStore.getPresetName(feedId, sourceId, preset.id),
      }));

    const filteredNamedPresets = filteredPresets.filter(
      (preset) => preset.preset_type !== 'single_share_preset',
    );

    return sortBy(filteredNamedPresets, [
      (preset) => preset.preset_type !== 'crossbeam_default', // Ensures crossbeam_default is first
      (preset) => preset.name?.toLowerCase() ?? '', // Sort the rest alphabetically
    ]);
  }

  async function redirectAndSuccess(
    name: string | null,
    data?: DataSharePreset,
    isNewPreset?: boolean,
  ) {
    if (data) {
      const presetType = data.preset_type ?? undefined;
      const metadata: {
        is_new_preset: boolean;
        id?: number;
        name?: string | null;
      } = { is_new_preset: isNewPreset ?? false };
      if (data.id != null) metadata.id = data.id;
      if (presetType === 'named_preset' && data.name != null) {
        metadata.name = data.name;
      }
      iteratively.userDefinedSharingPreset({
        event_site: eventSite ?? EVENT_SITES.DATA_PRESETS_MODAL,
        preset_type: presetType,
        metadata,
      });
    }
    await handleSuccess(
      data,
      `${name || 'Custom field selection'} has been successfully ${isNewPreset ? 'created' : 'edited'}`,
    );
    if (data?.id && onSuccess) {
      onSuccess(data.id, !!isNewPreset);
    }
    isLoading.value = false;
  }

  async function handleSuccess(data?: DataSharePreset, message?: string) {
    if (data) await dataSharePresetsStore.refreshDataSharePresetsStore();
    if (message) addSuccessFlash({ message });
    onClose?.();
  }

  function handleErrorAndSuccess(
    errorMessage: string,
    error?: { errors: string[] },
    onSuccess?: () => void,
  ) {
    if (error) {
      addErrorFlash({
        message: error.errors?.[0] || errorMessage,
      });
      isLoading.value = false;
      return;
    }
    onSuccess?.();
  }

  return {
    presetName,
    sortedPresets,
    isDefaultPreset,
    hasDefaultPresets,
    sourceFieldIdsByMdm,
    currentSourceFieldIds,
    isToggledNewPreset,
    isLoading: !ready || isLoading,
    createPreset,
    editPreset,
    deletePreset,
    getFieldOptions,
    resetToDefault,
  };
}
